// Annotation overlay for adding labels to rooms

import type { Room, Point } from "../types.ts";

const ROOM_SUGGESTIONS = [
  // Tomb
  'entrance', 'antechamber', 'burial chamber', 'sarcophagus room', 'treasure vault',
  'trapped corridor', 'false tomb', 'guardian chamber', 'tomb entrance',
  // Temple
  'shrine', 'altar room', 'sanctuary', 'nave', 'vestibule', 'chapel', 'prayer hall',
  // Cave
  'cavern', 'pool chamber', 'crystal cave', 'fungal grove', 'underground lake',
  // Fortress
  'guard room', 'barracks', 'armory', 'prison cells', 'great hall', 'throne room',
  // Lair
  'nest', 'feeding chamber', 'hoard room', 'web-filled passage', 'den',
];

/**
 * AnnotationOverlay manages a text input overlay for annotating rooms.
 * The input appears at the room's center with hand-drawn aesthetic styling.
 */
export class AnnotationOverlay {
  private input: HTMLInputElement;
  private suggestionsDiv: HTMLDivElement;
  private currentRoomId: string | null = null;
  private confirmCallback: ((roomId: string, label: string) => void) | null = null;
  private selectedSuggestionIndex: number = -1;

  constructor(container: HTMLElement) {
    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "annotation-input";
    this.input.style.position = "absolute";
    this.input.style.display = "none";
    this.input.style.zIndex = "1000";

    // Hand-drawn aesthetic styling
    this.input.style.fontFamily = "'Caveat', 'Comic Sans MS', cursive";
    this.input.style.fontSize = "18px";
    this.input.style.border = "2px solid #333";
    this.input.style.borderRadius = "0";
    this.input.style.background = "#fffef0";
    this.input.style.padding = "4px 8px";
    this.input.style.boxShadow = "2px 2px 4px rgba(0, 0, 0, 0.2)";
    this.input.style.outline = "none";
    this.input.style.minWidth = "120px";
    this.input.style.textAlign = "center";

    container.appendChild(this.input);

    // Create suggestions dropdown
    this.suggestionsDiv = document.createElement("div");
    this.suggestionsDiv.className = "annotation-suggestions";
    this.suggestionsDiv.style.position = "absolute";
    this.suggestionsDiv.style.display = "none";
    this.suggestionsDiv.style.zIndex = "1001";
    this.suggestionsDiv.style.maxHeight = "200px";
    this.suggestionsDiv.style.overflowY = "auto";
    this.suggestionsDiv.style.border = "2px solid #333";
    this.suggestionsDiv.style.background = "#fffef0";
    this.suggestionsDiv.style.boxShadow = "2px 2px 4px rgba(0, 0, 0, 0.2)";
    this.suggestionsDiv.style.fontFamily = "'Caveat', 'Comic Sans MS', cursive";
    this.suggestionsDiv.style.fontSize = "16px";
    this.suggestionsDiv.style.minWidth = "120px";
    container.appendChild(this.suggestionsDiv);

    // Handle input changes for filtering suggestions
    this.input.addEventListener("input", () => {
      this.updateSuggestions();
    });

    // Handle keyboard events
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (this.selectedSuggestionIndex >= 0) {
          this.selectCurrentSuggestion();
        } else {
          this.confirm();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.hide();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        this.moveSuggestionSelection(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.moveSuggestionSelection(-1);
      }
    });

    // Hide when clicking outside
    this.input.addEventListener("blur", () => {
      // Small delay to allow click events to process
      setTimeout(() => {
        if (document.activeElement !== this.input) {
          this.hide();
        }
      }, 100);
    });
  }

  /**
   * Show the annotation input at the specified position for the given room.
   * @param room The room to annotate
   * @param position The pixel position (typically room center) to display the input
   */
  show(room: Room, position: Point): void {
    this.currentRoomId = room.id;
    this.input.value = room.label || "";

    // Position the input centered at the given point
    // Adjust by half the input's width to center it
    this.input.style.left = `${position.x - 60}px`;  // Approximate centering
    this.input.style.top = `${position.y - 12}px`;   // Center vertically on text
    this.input.style.display = "block";

    // Position suggestions below the input
    this.suggestionsDiv.style.left = `${position.x - 60}px`;
    this.suggestionsDiv.style.top = `${position.y + 20}px`;

    // Focus and select all text for easy editing
    this.input.focus();
    this.input.select();

    // Show initial suggestions
    this.updateSuggestions();
  }

  /**
   * Hide the annotation input
   */
  hide(): void {
    this.input.style.display = "none";
    this.suggestionsDiv.style.display = "none";
    this.currentRoomId = null;
    this.input.value = "";
    this.selectedSuggestionIndex = -1;
  }

  /**
   * Register a callback for when the annotation is confirmed (Enter key pressed)
   * @param callback Function called with roomId and label text
   */
  onConfirm(callback: (roomId: string, label: string) => void): void {
    this.confirmCallback = callback;
  }

  /**
   * Confirm the current annotation and invoke the callback
   */
  private confirm(): void {
    if (this.currentRoomId && this.confirmCallback) {
      const label = this.input.value.trim();
      this.confirmCallback(this.currentRoomId, label);
    }
    this.hide();
  }

  /**
   * Update the suggestions dropdown based on current input value
   */
  private updateSuggestions(): void {
    const inputValue = this.input.value.toLowerCase().trim();

    // Filter suggestions based on input (case-insensitive contains match)
    const filteredSuggestions = inputValue === ""
      ? ROOM_SUGGESTIONS
      : ROOM_SUGGESTIONS.filter(suggestion =>
          suggestion.toLowerCase().includes(inputValue)
        );

    // Clear previous suggestions
    this.suggestionsDiv.innerHTML = "";
    this.selectedSuggestionIndex = -1;

    if (filteredSuggestions.length === 0) {
      this.suggestionsDiv.style.display = "none";
      return;
    }

    // Show filtered suggestions
    filteredSuggestions.forEach((suggestion, index) => {
      const suggestionItem = document.createElement("div");
      suggestionItem.textContent = suggestion;
      suggestionItem.style.padding = "6px 12px";
      suggestionItem.style.cursor = "pointer";
      suggestionItem.style.borderBottom = "1px solid #ddd";

      // Hover effect
      suggestionItem.addEventListener("mouseenter", () => {
        suggestionItem.style.background = "#f0e68c";
      });
      suggestionItem.addEventListener("mouseleave", () => {
        if (this.selectedSuggestionIndex !== index) {
          suggestionItem.style.background = "#fffef0";
        }
      });

      // Click to select
      suggestionItem.addEventListener("mousedown", (e) => {
        e.preventDefault(); // Prevent input blur
        this.input.value = suggestion;
        this.suggestionsDiv.style.display = "none";
        this.input.focus();
      });

      this.suggestionsDiv.appendChild(suggestionItem);
    });

    this.suggestionsDiv.style.display = "block";
  }

  /**
   * Move the suggestion selection up or down
   */
  private moveSuggestionSelection(delta: number): void {
    const items = this.suggestionsDiv.children;
    if (items.length === 0) return;

    // Clear previous selection
    if (this.selectedSuggestionIndex >= 0 && this.selectedSuggestionIndex < items.length) {
      (items[this.selectedSuggestionIndex] as HTMLElement).style.background = "#fffef0";
    }

    // Update selection index
    this.selectedSuggestionIndex = Math.max(0, Math.min(items.length - 1, this.selectedSuggestionIndex + delta));

    // Highlight new selection
    const selectedItem = items[this.selectedSuggestionIndex] as HTMLElement;
    selectedItem.style.background = "#f0e68c";
    selectedItem.scrollIntoView({ block: "nearest" });
  }

  /**
   * Select the currently highlighted suggestion
   */
  private selectCurrentSuggestion(): void {
    const items = this.suggestionsDiv.children;
    if (this.selectedSuggestionIndex >= 0 && this.selectedSuggestionIndex < items.length) {
      const selectedText = items[this.selectedSuggestionIndex].textContent || "";
      this.input.value = selectedText;
      this.suggestionsDiv.style.display = "none";
      this.selectedSuggestionIndex = -1;
      this.confirm();
    }
  }
}
